import { lazy } from 'react'

import type { RouteObject } from 'react-router'
import { createBrowserRouter, Navigate } from 'react-router'

import {
  AcceptInviteUrl,
  ComplianceOverviewUrl,
  DashboardUrl,
  FreshbooksUrl,
  LoginUrl,
  MaintenanceUrl,
  ManageSubscriptionsUrl,
  PasswordResetUrl,
  SendPasswordResetUrl,
  SharedReportUrl,
  SignUpAboutYouUrl,
  SignUpCreateTeamUrl,
  SignUpInviteUrl,
  SignUpStepOneUrl,
  SignUpStepThreeUrl,
  SignUpStepTwoUrl,
  SignupUrl,
  SplashMessageUrl,
  StartUrl,
  UnlimitedTripsUrl
} from 'constants/RouteUrls'
import NotFoundPage from 'routes/NotFoundPage'
import SideMenuLayout from 'routes/SideMenuLayout'
import AccountRoutes from 'routes/AccountRoutes'
import PersonalDashboardRoutes from 'routes/PersonalDashboardRoutes'
import TeamSpecificRoutes from 'routes/TeamSpecificRoutes'
import TeamsDashboardRoutes from 'routes/TeamsDashboardRoutes'
import AuthLandingPage from 'pages/auth/AuthLandingPage'
import AuthRoutes from 'routes/AuthRoutes'
import ProtectedRoute from 'routes/ProtectedRoute'
import RouteLayout from 'routes/RouteLayout'
import ErrorBoundaryRoute from 'routes/ErrorBoundaryRoute'
import { lazyLoadHelper } from 'routes/resources'
import PurplePasswordResetPage from 'pages/auth/purple/PurplePasswordResetPage'
import CompanyToggle from 'core/widgets/CompanyToggle'
import AppLoadingIndicator from 'core/widgets/app-loader/AppLoadingIndicator'
import Suspense from 'core/components/suspense/Suspense'

const SplashMessage = lazy(() =>
  lazyLoadHelper(() => import('pages/dashboard/home/SplashMessage'))
)
const AcceptTeamInvitePage = lazy(() =>
  lazyLoadHelper(
    () => import('pages/dashboard/accept-invite/AcceptTeamInvitePage')
  )
)
const AboutYouPage = lazy(() =>
  lazyLoadHelper(() => import('pages/sign-up/AboutYouPage'))
)
const FirstStepPage = lazy(() =>
  lazyLoadHelper(() => import('pages/sign-up/FirstStep'))
)
const SecondStepPage = lazy(() =>
  lazyLoadHelper(() => import('pages/sign-up/SecondStep'))
)
const CreateTeamPage = lazy(() =>
  lazyLoadHelper(() => import('pages/sign-up/CreateTeamPage'))
)
const ThirdStepPage = lazy(() =>
  lazyLoadHelper(() => import('pages/sign-up/ThirdStep'))
)
const UnlimitedTripsPage = lazy(() =>
  lazyLoadHelper(() => import('pages/sign-up/UnlimitedTrips'))
)
const InviteMembersPage = lazy(() =>
  lazyLoadHelper(() => import('pages/sign-up/InviteMembersPage'))
)
const ReportPage = lazy(() =>
  lazyLoadHelper(() => import('pages/shared/report/ReportPage'))
)
const Freshbooks = lazy(() => lazyLoadHelper(() => import('app/Freshbooks')))
const EmailSubscriptionPage = lazy(() =>
  lazyLoadHelper(
    () => import('pages/account/email-subscription/EmailSubscriptionPage')
  )
)
const PasswordResetPage = lazy(() =>
  lazyLoadHelper(() => import('pages/auth/PasswordResetPage'))
)
const PasswordEditPage = lazy(() =>
  lazyLoadHelper(() => import('pages/auth/PasswordEditPage'))
)
const MaintenancePage = lazy(() =>
  lazyLoadHelper(() => import('routes/MaintenancePage'))
)

// https://www.notion.so/everlance/Authentication-7013b73145c44586b23d0fac6c0eb9f1
export const routes: RouteObject[] = [
  {
    element: <RouteLayout />,
    errorElement: <ErrorBoundaryRoute />,
    loader: () => <AppLoadingIndicator />,
    children: [
      {
        path: '/',
        element: (
          <ProtectedRoute>
            <SideMenuLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            path: '/account/compliance',
            element: <Navigate to={ComplianceOverviewUrl} replace />
          },
          {
            path: '/',
            element: <Navigate to={DashboardUrl} replace />
          },
          {
            path: '/*',
            element: <PersonalDashboardRoutes />
          },
          {
            path: '/account/*',
            element: <AccountRoutes />
          },
          {
            path: '/team/*',
            element: <TeamSpecificRoutes />
          },
          {
            path: '/teams/*',
            element: <TeamsDashboardRoutes />
          },
          {
            path: '*',
            element: <NotFoundPage />
          }
        ]
      },
      // Signup flow - All users land in this page
      {
        path: SignUpAboutYouUrl,
        element: (
          <Suspense variant="about-you">
            <ProtectedRoute>
              <AboutYouPage />
            </ProtectedRoute>
          </Suspense>
        )
      },
      // 1099 user account flow
      {
        path: SignUpStepOneUrl,
        element: (
          <Suspense variant="sign-up">
            <ProtectedRoute>
              <FirstStepPage />
            </ProtectedRoute>
          </Suspense>
        )
      },
      {
        path: SignUpStepTwoUrl,
        element: (
          <Suspense variant="sign-up">
            <ProtectedRoute>
              <SecondStepPage />
            </ProtectedRoute>
          </Suspense>
        )
      },
      {
        path: SignUpStepThreeUrl,
        element: (
          <Suspense variant="sign-up">
            <ProtectedRoute>
              <ThirdStepPage />
            </ProtectedRoute>
          </Suspense>
        )
      },
      {
        path: UnlimitedTripsUrl,
        element: (
          <Suspense variant="sign-up">
            <ProtectedRoute>
              <UnlimitedTripsPage />
            </ProtectedRoute>
          </Suspense>
        )
      },
      // Team creation screens
      {
        path: SignUpCreateTeamUrl,
        element: (
          <Suspense variant="sign-up">
            <ProtectedRoute>
              <CreateTeamPage />
            </ProtectedRoute>
          </Suspense>
        )
      },
      {
        path: SignUpInviteUrl,
        element: (
          <Suspense variant="sign-up">
            <ProtectedRoute>
              <InviteMembersPage />
            </ProtectedRoute>
          </Suspense>
        )
      },
      // Misc routes
      {
        path: SharedReportUrl,
        element: (
          <Suspense variant="report" isChildPage withBanner>
            <ReportPage />
          </Suspense>
        )
      },
      {
        path: SplashMessageUrl,
        element: (
          <Suspense variant="app">
            <ProtectedRoute>
              <SplashMessage />
            </ProtectedRoute>
          </Suspense>
        )
      },
      {
        path: AcceptInviteUrl,
        element: (
          <Suspense variant="accept-invite" isChildPage>
            <ProtectedRoute>
              <AcceptTeamInvitePage />
            </ProtectedRoute>
          </Suspense>
        )
      },
      {
        path: FreshbooksUrl,
        element: (
          <Suspense variant="app">
            <Freshbooks />
          </Suspense>
        )
      },
      {
        path: SendPasswordResetUrl,
        element: (
          <Suspense variant="auth">
            <CompanyToggle company="motus">
              <PurplePasswordResetPage />
            </CompanyToggle>
            <CompanyToggle company="everlance">
              <PasswordResetPage />
            </CompanyToggle>
          </Suspense>
        )
      },
      {
        path: PasswordResetUrl,
        element: (
          <Suspense variant="auth">
            <PasswordEditPage />
          </Suspense>
        )
      },
      {
        path: ManageSubscriptionsUrl,
        element: (
          <Suspense variant="auth">
            <EmailSubscriptionPage />
          </Suspense>
        )
      },
      // Auth pages
      {
        path: '/auth/',
        element: (
          <ProtectedRoute validateAuthOnly>
            <AuthLandingPage />
          </ProtectedRoute>
        ),
        children: [
          {
            path: '*',
            element: <AuthRoutes />
          }
        ]
      },
      // Legacy routes maintenance
      {
        path: '/start',
        element: <Navigate to={StartUrl} replace />
      },
      {
        path: '/login',
        element: <Navigate to={LoginUrl} replace />
      },
      {
        path: '/signup',
        element: <Navigate to={SignupUrl} replace />
      },
      {
        path: AcceptInviteUrl,
        element: (
          <Suspense variant="accept-invite" isChildPage>
            <ProtectedRoute>
              <AcceptTeamInvitePage />
            </ProtectedRoute>
          </Suspense>
        )
      },
      {
        path: FreshbooksUrl,
        element: (
          <Suspense variant="app">
            <Freshbooks />
          </Suspense>
        )
      },
      {
        path: SendPasswordResetUrl,
        element: (
          <Suspense variant="auth">
            <CompanyToggle company="motus">
              <PurplePasswordResetPage />
            </CompanyToggle>
            <CompanyToggle company="everlance">
              <PasswordResetPage />
            </CompanyToggle>
          </Suspense>
        )
      },
      {
        path: PasswordResetUrl,
        element: (
          <Suspense variant="auth">
            <PasswordEditPage />
          </Suspense>
        )
      },
      {
        path: ManageSubscriptionsUrl,
        element: (
          <Suspense variant="auth">
            <EmailSubscriptionPage />
          </Suspense>
        )
      },
      {
        path: '*',
        element: <NotFoundPage />
      },
      {
        path: MaintenanceUrl,
        element: (
          <Suspense variant="app">
            <MaintenancePage />
          </Suspense>
        )
      }
    ]
  }
]

export const appRoutes = createBrowserRouter(routes)
